/**
 * Main App Component
 * Sets up routing and global providers
 */

import { Routes, Route } from 'react-router-dom';
import { Suspense, lazy } from 'react';

import { RootLayout } from './layouts/RootLayout';
import { AuthLayout } from './layouts/AuthLayout';
import { ProtectedLayout } from './layouts/ProtectedLayout';
import { AdminLayout } from './layouts/AdminLayout';
import { PageLoader } from './components/PageLoader';

// Lazy load pages for code splitting
const HomePage = lazy(() => import('./pages/HomePage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const AISearchPage = lazy(() => import('./pages/AISearchPage'));
const SchoolProfilePage = lazy(() => import('./pages/SchoolProfilePage'));
const ComparisonPage = lazy(() => import('./pages/ComparisonPage'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const SignupPage = lazy(() => import('./pages/auth/SignupPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const DashboardPage = lazy(() => import('./pages/user/DashboardPage'));
const SavedSchoolsPage = lazy(() => import('./pages/user/SavedSchoolsPage'));
const SettingsPage = lazy(() => import('./pages/user/SettingsPage'));
const ClaimSchoolPage = lazy(() => import('./pages/user/ClaimSchoolPage'));
const CommunityPage = lazy(() => import('./pages/community/CommunityPage'));
const ConversationPage = lazy(() => import('./pages/community/ConversationPage'));
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'));
const AdminSchoolsPage = lazy(() => import('./pages/admin/AdminSchoolsPage'));
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'));
const AdminClaimsPage = lazy(() => import('./pages/admin/AdminClaimsPage'));
const AdminAIPage = lazy(() => import('./pages/admin/AdminAIPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Static pages
const AboutPage = lazy(() => import('./pages/AboutPage'));
const HelpPage = lazy(() => import('./pages/HelpPage'));
const PrivacyPage = lazy(() => import('./pages/legal/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/legal/TermsPage'));
const CookiePolicyPage = lazy(() => import('./pages/legal/CookiePolicyPage'));

export function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes with main layout */}
        <Route element={<RootLayout />}>
          <Route index element={<HomePage />} />
          <Route path="search" element={<SearchPage />} />
          <Route path="ai-search" element={<AISearchPage />} />
          <Route path="school/:schoolId" element={<SchoolProfilePage />} />
          <Route path="compare" element={<ComparisonPage />} />
          <Route path="community" element={<CommunityPage />} />
          <Route path="community/:conversationId" element={<ConversationPage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="help" element={<HelpPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="terms" element={<TermsPage />} />
          <Route path="cookies" element={<CookiePolicyPage />} />
        </Route>

        {/* Auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="login" element={<LoginPage />} />
          <Route path="signup" element={<SignupPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="reset-password" element={<ResetPasswordPage />} />
        </Route>

        {/* Protected user routes */}
        <Route element={<ProtectedLayout />}>
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="saved-schools" element={<SavedSchoolsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="claim-school/:schoolId" element={<ClaimSchoolPage />} />
        </Route>

        {/* Admin routes */}
        <Route path="admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboardPage />} />
          <Route path="schools" element={<AdminSchoolsPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="claims" element={<AdminClaimsPage />} />
          <Route path="ai" element={<AdminAIPage />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
